import React, { useState, useEffect } from 'react';
import { 
  Search, Eye, Download, FileText, User, Phone, Mail, 
  Building, Briefcase, Calendar, ChevronDown, ChevronUp,
  CheckCircle, XCircle, Clock, Filter, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/api/api';
import { toast } from 'sonner';

const StaffDocumentsReport = () => {
  const [staffList, setStaffList] = useState([]);
  const [filteredStaff, setFilteredStaff] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [staffTypeFilter, setStaffTypeFilter] = useState('all');
  const [documentStatusFilter, setDocumentStatusFilter] = useState('all');
  const [expandedStaff, setExpandedStaff] = useState({});
  const [staffDocuments, setStaffDocuments] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    filterStaff();
  }, [staffList, searchTerm, departmentFilter, staffTypeFilter, documentStatusFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch staff list
      const staffRes = await api.get('/staff/');
      const staffData = Array.isArray(staffRes.data) ? staffRes.data : staffRes.data.staff || [];
      setStaffList(staffData);
      
      // Extract unique departments
      const depts = [...new Set(staffData.map(s => s.department).filter(Boolean))];
      setDepartments(depts);
      
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load staff data');
    } finally {
      setLoading(false);
    }
  };

  const filterStaff = () => {
    let filtered = [...staffList];
    
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(s => 
        s.full_name?.toLowerCase().includes(term) ||
        s.employee_id?.toLowerCase().includes(term) ||
        s.email?.toLowerCase().includes(term) ||
        s.phone?.includes(term)
      );
    }
    
    // Department filter
    if (departmentFilter !== 'all') {
      filtered = filtered.filter(s => s.department === departmentFilter);
    }
    
    // Staff type filter
    if (staffTypeFilter !== 'all') {
      filtered = filtered.filter(s => {
        const type = s.staff_type || (s.roles?.includes('Teacher') ? 'Teaching' : 'Non-Teaching');
        return type === staffTypeFilter;
      });
    }
    
    setFilteredStaff(filtered);
  };

  const fetchStaffDocuments = async (staffId) => {
    try {
      const res = await api.get(`/staff/${staffId}/documents`);
      setStaffDocuments(prev => ({
        ...prev,
        [staffId]: res.data.documents || []
      }));
    } catch (error) {
      console.error('Error fetching documents:', error);
      // Try alternate endpoint
      try {
        const res = await api.get(`/transcripts/staff?entity_id=${staffId}`);
        setStaffDocuments(prev => ({
          ...prev,
          [staffId]: Array.isArray(res.data) ? res.data : []
        }));
      } catch (err) {
        setStaffDocuments(prev => ({
          ...prev,
          [staffId]: []
        }));
      }
    }
  };

  const toggleExpand = async (staffId) => {
    const isExpanding = !expandedStaff[staffId];
    setExpandedStaff(prev => ({
      ...prev,
      [staffId]: isExpanding
    }));
    
    // Fetch documents if expanding and not already loaded
    if (isExpanding && !staffDocuments[staffId]) {
      await fetchStaffDocuments(staffId);
    }
  };

  const getStaffType = (staff) => {
    if (staff.staff_type) return staff.staff_type;
    const roles = staff.roles || [];
    if (roles.includes('Teacher') || roles.includes('HOD') || roles.includes('Principal')) {
      return 'Teaching';
    }
    return 'Non-Teaching';
  };

  const getDocumentStatus = (doc) => {
    if (doc.status === 'Verified') return { color: 'bg-green-100 text-green-700', icon: CheckCircle };
    if (doc.status === 'Rejected') return { color: 'bg-red-100 text-red-700', icon: XCircle };
    return { color: 'bg-yellow-100 text-yellow-700', icon: Clock };
  };

  const downloadDocument = async (doc) => {
    try {
      if (doc.file_data) {
        const link = document.createElement('a');
        link.href = doc.file_data;
        link.download = doc.file_name || doc.title || 'document';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        toast.error('Document file not available');
      }
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  const stats = {
    total: staffList.length,
    teaching: staffList.filter(s => getStaffType(s) === 'Teaching').length,
    nonTeaching: staffList.filter(s => getStaffType(s) === 'Non-Teaching').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Staff Documents Report</h1>
          <p className="text-sm text-gray-500 mt-1">View staff details and their uploaded documents</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <User className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Staff</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Briefcase className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Teaching Staff</p>
                <p className="text-2xl font-bold text-gray-900">{stats.teaching}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <Building className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Non-Teaching Staff</p>
                <p className="text-2xl font-bold text-gray-900">{stats.nonTeaching}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <Input
                placeholder="Search by name, ID, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(dept => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={staffTypeFilter} onValueChange={setStaffTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="Teaching">Teaching</SelectItem>
                <SelectItem value="Non-Teaching">Non-Teaching</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => {
              setSearchTerm('');
              setDepartmentFilter('all');
              setStaffTypeFilter('all');
            }}>
              <Filter className="h-4 w-4 mr-2" />
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Staff List with Expandable Documents */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Staff List ({filteredStaff.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {filteredStaff.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <User className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p>No staff found matching your criteria</p>
            </div>
          ) : (
            <div className="divide-y">
              {filteredStaff.map((staff) => (
                <div key={staff.id} className="border-b last:border-b-0">
                  {/* Staff Row */}
                  <div 
                    className="p-4 hover:bg-gray-50 cursor-pointer flex items-center justify-between"
                    onClick={() => toggleExpand(staff.id)}
                  >
                    <div className="flex items-center gap-4">
                      {/* Photo */}
                      <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center overflow-hidden">
                        {staff.photo || staff.photo_url || staff.profile_photo ? (
                          <img 
                            src={staff.photo || staff.photo_url || staff.profile_photo} 
                            alt={staff.full_name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <User className="h-6 w-6 text-gray-400" />
                        )}
                      </div>
                      
                      {/* Info */}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-gray-900">{staff.full_name}</p>
                          <span className={`px-2 py-0.5 text-xs rounded ${
                            getStaffType(staff) === 'Teaching' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-purple-100 text-purple-700'
                          }`}>
                            {getStaffType(staff)}
                          </span>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                          <span className="font-mono">{staff.employee_id}</span>
                          <span>{staff.designation}</span>
                          <span>{staff.department}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="hidden md:flex items-center gap-4 text-sm text-gray-500">
                        {staff.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {staff.phone}
                          </span>
                        )}
                        {staff.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {staff.email}
                          </span>
                        )}
                      </div>
                      <Button variant="ghost" size="sm">
                        {expandedStaff[staff.id] ? (
                          <ChevronUp className="h-5 w-5" />
                        ) : (
                          <ChevronDown className="h-5 w-5" />
                        )}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Expanded Details & Documents */}
                  {expandedStaff[staff.id] && (
                    <div className="px-4 pb-4 bg-gray-50">
                      <div className="grid md:grid-cols-2 gap-6 mt-2">
                        {/* Staff Details */}
                        <div className="bg-white p-4 rounded-lg border">
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <User className="h-4 w-4" />
                            Staff Details
                          </h4>
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            <div>
                              <p className="text-gray-500">Full Name</p>
                              <p className="font-medium">{staff.full_name}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Employee ID</p>
                              <p className="font-medium font-mono">{staff.employee_id}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Email</p>
                              <p className="font-medium">{staff.email || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Phone</p>
                              <p className="font-medium">{staff.phone || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Department</p>
                              <p className="font-medium">{staff.department || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Designation</p>
                              <p className="font-medium">{staff.designation || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Staff Type</p>
                              <p className="font-medium">{getStaffType(staff)}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Joining Date</p>
                              <p className="font-medium">{staff.joining_date || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Blood Group</p>
                              <p className="font-medium">{staff.blood_group || 'N/A'}</p>
                            </div>
                            <div>
                              <p className="text-gray-500">Gender</p>
                              <p className="font-medium">{staff.gender || 'N/A'}</p>
                            </div>
                            <div className="col-span-2">
                              <p className="text-gray-500">Address</p>
                              <p className="font-medium">{staff.address || 'N/A'}</p>
                            </div>
                          </div>
                        </div>
                        
                        {/* Documents */}
                        <div className="bg-white p-4 rounded-lg border">
                          <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                            <FileText className="h-4 w-4" />
                            Documents ({staffDocuments[staff.id]?.length || 0})
                          </h4>
                          
                          {!staffDocuments[staff.id] ? (
                            <div className="flex items-center justify-center py-4">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                            </div>
                          ) : staffDocuments[staff.id].length === 0 ? (
                            <div className="text-center py-6 text-gray-500">
                              <FileText className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                              <p>No documents uploaded</p>
                            </div>
                          ) : (
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                              {staffDocuments[staff.id].map((doc, idx) => {
                                const status = getDocumentStatus(doc);
                                const StatusIcon = status.icon;
                                return (
                                  <div 
                                    key={doc.id || idx}
                                    className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                                  >
                                    <div className="flex items-center gap-3">
                                      <FileText className="h-5 w-5 text-gray-400" />
                                      <div>
                                        <p className="text-sm font-medium">{doc.title || doc.document_type || doc.name}</p>
                                        <p className="text-xs text-gray-500">
                                          {doc.created_at?.split('T')[0] || doc.uploaded_at?.split('T')[0] || 'N/A'}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className={`px-2 py-0.5 text-xs rounded flex items-center gap-1 ${status.color}`}>
                                        <StatusIcon className="h-3 w-3" />
                                        {doc.status || 'Pending'}
                                      </span>
                                      {doc.file_data && (
                                        <Button 
                                          variant="ghost" 
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            downloadDocument(doc);
                                          }}
                                        >
                                          <Download className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default StaffDocumentsReport;
